import gql from 'graphql-tag';

import GraphQLClient from '../../../adapter/GraphQLClient';
import {
  AssignmentQuery,
  MyAssignmentsCountQuery,
  MyAssignmentsQuery,
  SingleAssignmentFragment,
} from './../../../graphql/generated/graphql_offer';
import { reputationFragment } from './GqlFragments';

export const assignnmentFragment = gql`
  ${reputationFragment}
  fragment SingleAssignment on Assignment {
    id
    status
    candidatedStatus
    assignedStatus
    offer {
      id
      jobId
      name
      type
      matchingFormat
      expectedDue
      expressDelivery
      jobRevisedAt
      rewardCurrency
      rewardAmount
      organization {
        id
        name
      }
      creativeFormats {
        id
        network
        creativeType
        resizeBaseId
        placement {
          key
          name
        }
        attribute {
          aspectRatio {
            width
            height
          }
          size {
            width
            height
          }
          playbackTime {
            seconds
          }
          fastStart
        }
      }
    }
    updatedAt
    activeStatusUpdated
    rewardAmount
    reputation {
      ...SingleReputation
    }
    reputationToOffer {
      ...SingleReputation
    }
  }
`;

export class AssignmentWebApi {
  private readonly graphqlClient: GraphQLClient;

  constructor({ graphqlClient }: { graphqlClient: GraphQLClient }) {
    this.graphqlClient = graphqlClient;
  }

  public search = async (
    filterState: string,
    cursor: string | null,
  ): Promise<{ items: SingleAssignmentFragment[]; endCursor: string | null }> => {
    const result: MyAssignmentsQuery = (await this.graphqlClient.query(
      gql`
        ${assignnmentFragment}
        query MyAssignments($after: String, $filterState: MyAssignmentsState) {
          myAssignments(after: $after, filterState: $filterState, first: 20) {
            nodes {
              ...SingleAssignment
            }
            pageInfo {
              endCursor
              hasNextPage
            }
          }
        }
      `,
      {
        after: cursor,
        filterState: filterState,
      },
    )) as MyAssignmentsQuery;

    const { nodes, pageInfo } = result.myAssignments;
    const res = nodes?.filter((n): n is SingleAssignmentFragment => !!n);
    if (nodes && res && nodes.length === res.length) {
      return {
        items: res,
        endCursor: pageInfo.hasNextPage && pageInfo.endCursor ? pageInfo.endCursor : null,
      };
    } else {
      throw new Error('result.offers.edges must be an array.');
    }
  };

  public getCount = async (filterState: string): Promise<number> => {
    const res: MyAssignmentsCountQuery = (await this.graphqlClient.query(
      gql`
        query MyAssignmentsCount($filterState: MyAssignmentsState) {
          myAssignmentsCount(filterState: $filterState)
        }
      `,
      {
        filterState: filterState,
      },
    )) as MyAssignmentsCountQuery;
    return res.myAssignmentsCount;
  };

  public get = async (id: string): Promise<SingleAssignmentFragment> => {
    const res: AssignmentQuery = (await this.graphqlClient.query(
      gql`
        ${assignnmentFragment}
        query Assignment($id: ID!) {
          assignment(id: $id) {
            ...SingleAssignment
          }
        }
      `,
      {
        id: id,
      },
    )) as AssignmentQuery;
    return res.assignment;
  };

  public updateViewedAt = async (id: string): Promise<void> => {
    await this.graphqlClient.query(
      gql`
        mutation viewAssignment($id: ID!) {
          viewAssignment(input: { id: $id }) {
            assignment {
              id
            }
          }
        }
      `,
      {
        id: id,
      },
    );
  };
}
